<template>
  <div>
    <page-toolbar>
      <kendo-button v-if="isMaster && canEdit" icon="add" title="Add Row" @click="addRecord">
      </kendo-button>
    </page-toolbar>

    <kendo-grid
      height="calc(100vh - 170px)"
      ref="contractGrid"
      id="contractGrid"
      :editable="!isReadOnly && 'inline'"
      :dataSource="dataSource"
      :columns="columns"
      :pageable="true"
      :filterable="true"
      :sortable="true"
      :navigatable="true"
      :autoBind="true"
    ></kendo-grid>
  </div>
</template>
<script>
import $ from '@/helpers/jquery-bridge'
import {mapGetters} from 'vuex'
import {enableExportExcel} from '@/helpers/export-helper'
import {getDataItem} from '@/helpers/kendo-helpers'
import PageToolbar from '@/components/PageToolbar'
import security from '@/mixins/security'
import {jsonDropDownEditor, numericEditor, readOnlyEditor} from '@/helpers/kendo-editors'

export default {
  name: 'Future',
  mixins: [security],
  components: {
    PageToolbar,
  },
  mounted() {
    let grid = this.$refs.contractGrid.kendoWidget()
    enableExportExcel(grid, '/api/config/productContract/exportToExcel', $('.page_toolbar-item'))
  },
  computed: {
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    isMaster() {
      return this.clientId == 0
    },
    canEdit() {
      return ['SuperAdmin', 'Admin'].includes(this.currentRole)
    },
    isReadOnly() {
      return !this.canEdit && this.isMaster
    },
    grid() {
      return this.$refs.contractGrid
    },
    columns() {
      const editColumn = this.isMaster
        ? {
            command: 'edit',
            title: '&nbsp;',
            width: 60,
            hidden: !this.canEdit,
          }
        : {
            command: [{name: 'edit', visible: dataItem => dataItem.marketId != 1}],
            title: '&nbsp;',
            width: 60,
            hidden: !this.canEdit,
          }

      return [
        editColumn,
        {
          field: 'contractCode',
          title: 'Contract Code',
          width: 130,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'contractName',
          title: 'Contract Name',
          width: 150,
        },
        {
          field: 'exchangeId',
          title: 'Exchange',
          width: 150,
          attributes: {style: 'text-align: left;'},
          editable: false,
          editor: jsonDropDownEditor(
            'exchangeId',
            'exchange',
            '/api/config/ProductContract/getExchange',
          ),
          template: '#:exchange# ',
        },
        {
          field: 'market',
          title: 'Market Code',
          width: 150,
          editor: this.marketDropDownEditor,
          template: '#:market# ',
        },
        {
          field: 'ccyPairId',
          title: 'Ccy/Com Pair',
          width: 150,
          editor: this.ccyPairDropDownEditor,
          template: '#:ccyPair# ',
        },
        {
          field: 'unit',
          title: 'Unit',
          width: 100,
          attributes: {style: 'text-align: left;'},
          editor: readOnlyEditor,
        },
        {
          field: 'contractSize',
          title: 'Contract Size',
          width: 150,
          attributes: {style: 'text-align: right;'},
          format: '{0:n0}',
          editor: numericEditor(
            {
              format: 'n0',
              spinners: false,
              decimals: 0,
            },
            true,
          ),
        },
        {
          field: 'quotation',
          title: 'Quotation',
          width: 150,
        },
        {
          field: 'tickSize',
          title: 'Tick Size',
          width: 150,
          attributes: {style: 'text-align: right;'},
          format: '{0:n5}',
          editor: numericEditor(
            {
              format: 'n5',
              spinners: false,
              decimals: 5,
            },
            true,
          ),
        },
        {
          field: 'tickValue',
          title: 'Tick Value',
          attributes: {style: 'text-align: right;'},
          format: '{0:n0}',
          editor: numericEditor(
            {
              format: 'n2',
              spinners: false,
              decimals: 2,
            },
            true,
          ),

          width: 150,
        },
        {
          field: 'ccy',
          title: 'Ccy',
          width: 100,
          editor: readOnlyEditor,
        },
        {
          field: 'lastTradingRule',
          title: 'Last Trading Rule',
          width: 300,
          attributes: {style: 'text-align: left;'},
          editor: jsonDropDownEditor(
            'lastTradingRuleId',
            'tradingRule',
            '/api/config/ProductContract/getTradingRule',
          ),
          template: '#:lastTradingRule#',
        },
        {command: 'destroy', title: '&nbsp;', width: 60, hidden: !this.isMaster},
      ]
    },
    dataSource() {
      return new kendo.data.DataSource({
        type: 'aspnetmvc-ajax',
        serverPaging: true,
        serverFiltering: true,
        serverSorting: true,
        pageSize: 20,
        transport: {
          read: {
            type: 'GET',
            url: '/api/config/productContract',
            data: {
              clientId: this.clientId,
            },
            dataType: 'json',
          },
          create: {
            type: 'POST',
            url: '/api/config/productContract',
            data: {
              marketId: this.marketId,
              clientId: this.clientId,
            },
            dataType: 'json',
          },
          update: {
            type: 'PUT',
            url: '/api/config/productContract',
            data: {
              marketId: this.marketId,
              clientId: this.clientId,
            },
            dataType: 'json',
          },
          destroy: {
            type: 'DELETE',
            url: '/api/config/productContract',
            data: {
              marketId: this.marketId,
              clientId: this.clientId,
            },
            dataType: 'json',
          },
        },
        batch: false,
        schema: {
          errors: 'errors',
          total: 'total',
          data: 'data',
          model: {
            id: 'productContractId',
            fields: {
              productContractId: {type: 'number', editable: false},
              productId: {type: 'number'},
              contractCode: {type: 'string', editable: this.isMaster, validation: {required: true}},
              contractName: {type: 'string', editable: this.isMaster, validation: {required: true}},
              ccyPair: {type: 'string', editable: this.isMaster},
              exchange: {type: 'string', editable: this.isMaster},
              unit: {type: 'string'},
              exchangeId: {type: 'number', editable: this.isMaster},
              ccyPairId: {
                type: 'number',
                validation: {required: this.marketId == 1},
                editable: this.isMaster,
              },
              contractSize: {type: 'number', editable: this.isMaster},
              quotation: {type: 'string', editable: this.isMaster},
              tickSize: {type: 'number', editable: this.isMaster},
              tickValue: {type: 'number', editable: this.isMaster},
              ccy: {type: 'string', editable: this.isMaster},
              lastTradingRule: {type: 'string', editable: this.isMaster},
              marketId: {type: 'number', editable: this.isMaster},
              market: {type: 'string', editable: this.isMaster},
              lastTradingRuleId: {
                type: 'number',
                editable: this.isMaster,
                validation: {required: true},
              },
            },
          },
        },
        error: function() {
          let grid = $('#contractGrid').data('kendoGrid')
          grid.one('dataBinding', e => {
            e.preventDefault()
          })
        },
      })
    },
  },
  methods: {
    marketDropDownEditor(container, options) {
      return jsonDropDownEditor('marketId', 'market', '', {
        dataSource: {
          transport: {
            read: {
              url: '/api/config/ProductContract/getMarkets',
              contentType: 'application/json',
              type: 'GET',
            },
          },
        },
        change: async () => {
          const ccyPairDropDown = kendo.jQuery('#ccyPairId').getKendoDropDownList()
          await ccyPairDropDown.dataSource.read()
          ccyPairDropDown.select(0)
          ccyPairDropDown.trigger('change')
        },
      }).call(this, container, options)
    },
    ccyPairDropDownEditor(container, options) {
      return jsonDropDownEditor('ccyPairId', 'ccyPair', '', {
        dataSource: {
          transport: {
            read: {
              url: '/api/config/ProductContract/getCcyPair',
              data: () => ({
                marketId: options.model.marketId,
              }),
              contentType: 'application/json',
              type: 'GET',
            },
          },
        },
        filter: 'contains',
        change: e => {
          const dataItem = e.sender.dataItem()
          let rowItem = getDataItem(e.sender.element)
          rowItem.set('ccy', dataItem.baseCcy)
          rowItem.set('unit', dataItem.unit)
        },
      }).call(this, container, options)
    },
    addRecord() {
      let grid = this.$refs.contractGrid.kendoWidget()
      grid.addRow()
    },
    gridRefresh() {
      let grid = this.$refs.contractGrid.kendoWidget()
      grid.dataSource.read()
    },
    closeAndRefresh() {
      this.gridRefresh()
      this.closeModal()
    },
  },
}
</script>
